import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { RefreshCw, ArrowRight, ArrowLeft } from "lucide-react";

export default function OrderResult() {
  const [sp] = useSearchParams();
  const number = sp.get("number");
  const status = sp.get("status") || "";
  const [order, setOrder] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!number) return;
    api.get(`/orders/${number}`).then(({ data }) => setOrder(data)).catch(() => {});
  }, [number]);

  const retryPay = async () => {
    if (!number) return;
    setRetrying(true);
    try {
      const { data } = await api.post("/payu/checkout", { order_number: number });
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v ?? "";
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setRetrying(false);
    }
  };

  const headings = {
    failed: "Payment could not be completed",
    invalid: "We couldn't verify that payment",
    mismatch: "That transaction didn't match your order",
    unknown: "That transaction wasn't found",
  };
  const heading = headings[status] || "Payment status";

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="order-result-page">
      <div className="eyebrow text-gold-500">Payment</div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl text-ink-900">{heading}</h1>
      <p className="mt-6 text-ink-700 leading-relaxed">
        {status === "failed"
          ? "No money was captured for this transaction. You can retry the payment below or switch to Cash on Delivery from your account."
          : "If any amount was debited, it will be auto-refunded by your bank within 5–7 business days. You can safely retry."}
      </p>
      {number && (
        <div className="mt-6 inline-block border hairline px-4 py-2 font-mono text-sm tracking-wider">
          Order #{number}
        </div>
      )}
      {order && (
        <div className="mt-2 text-sm text-ink-500">Total ₹{order.total?.toFixed(0)} · {order.items?.length} item(s)</div>
      )}
      <div className="mt-10 flex flex-wrap gap-3 justify-center">
        <button
          onClick={retryPay}
          disabled={!number || retrying}
          className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="retry-pay-btn"
        >
          <RefreshCw size={14} /> {retrying ? "Redirecting…" : "Retry payment"}
        </button>
        <Link to="/account/orders" className="btn-gold-outline px-6 py-3 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2" data-testid="go-orders-btn">
          Go to my orders <ArrowRight size={12} />
        </Link>
        <Link to="/shop" className="text-xs uppercase tracking-[0.28em] text-ink-500 hover:text-ink-900 py-3 inline-flex items-center gap-2" data-testid="back-shop-btn">
          <ArrowLeft size={12} /> Back to shop
        </Link>
      </div>
    </div>
  );
}
